import { useCallback, useState } from 'react';
import type { Line } from '../../types/diagram.tsx';

/** There are is no transition being proposed */
type ProposedTransitionNone = {
	stage: 'none';
};

/** The proposed transition has started from `startPort` */
type ProposedTransitionStarted = {
	stage: 'started';
	line: Line;
	startPortKey: string;
};

/** The proposed transition can be completed to from a valid transition,
 *  going from `startPort` to `endPort` */
export type ProposedTransitionValid = {
	stage: 'valid';
	line: Line;
	startPortKey: string;
	endPortKey: string;
};

export type ProposedTransition =
	| ProposedTransitionNone
	| ProposedTransitionStarted
	| ProposedTransitionValid;

/** Handles the 'create new transition' feature.
 *
 * A _'proposal'_ is defined as new transition that is being drawn on the diagram
 * by the user and can exist in 3 stages `'none'`, `'started'`, and `'valid'` as per the
 * state shape of {@link ProposedTransition}
 */
export const useTransitionProposals = () => {
	const [transitionProposal, setTransitionProposal] = useState<ProposedTransition>({
		stage: 'none',
	});

	const onTransitionProposalCancel = useCallback(() => {
		setTransitionProposal({ stage: 'none' });
	}, []);

	const onTransitionProposalMove = useCallback((startPortKey: string, line: Line) => {
		setTransitionProposal((prev) => {
			if (prev.stage === 'none') {
				return { ...prev, stage: 'started', startPortKey, line };
			}

			return { ...prev, startPortKey, line };
		});
	}, []);

	const onTransitionProposalClose = useCallback((endPortKey: string | undefined) => {
		setTransitionProposal((prev) => {
			if (prev.stage === 'none') {
				return prev;
			}

			if (endPortKey === undefined) {
				return {
					stage: 'started',
					line: prev.line,
					startPortKey: prev.startPortKey,
				};
			}

			return { ...prev, stage: 'valid', endPortKey };
		});
	}, []);

	return [
		transitionProposal,
		{
			onTransitionProposalCancel,
			onTransitionProposalClose,
			onTransitionProposalMove,
		},
	] as const;
};
